/**
 * Data layer for `white-label-tracking-domain-cname-with-ssl`. Thin typed fetch wrappers over /api/v1/white-label-tracking-domain-cname-with-ssl.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface WhiteLabelTrackingDomainCnameWithSslRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListWhiteLabelTrackingDomainCnameWithSslResponse {
	count: number;
	items: WhiteLabelTrackingDomainCnameWithSslRow[];
}

export interface CreateWhiteLabelTrackingDomainCnameWithSslBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/white-label-tracking-domain-cname-with-ssl";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListWhiteLabelTrackingDomainCnameWithSslResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListWhiteLabelTrackingDomainCnameWithSslResponse>(res);
}

export async function create(body: CreateWhiteLabelTrackingDomainCnameWithSslBody): Promise<WhiteLabelTrackingDomainCnameWithSslRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<WhiteLabelTrackingDomainCnameWithSslRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const whiteLabelTrackingDomainCnameWithSslQueryKeys = {
	all: ["white-label-tracking-domain-cname-with-ssl"] as const,
	list: () => [...whiteLabelTrackingDomainCnameWithSslQueryKeys.all] as const,
	detail: (id: string) => [...whiteLabelTrackingDomainCnameWithSslQueryKeys.all, id] as const,
};
